import * as XLSX from 'xlsx';
import { fetchAllBookings } from '../services/bookingService';
import { fetchBookingsByPackageId } from '../services/packageService';


const formatDateForExcel = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return dateString;
  }
};


const formatPriceForExcel = (price) => {
  if (!price) return '0 SEK';
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price) + ' SEK';
};

export const exportBookingsToExcel = async (bookingsData = null, packageName = null) => {
  try {
    // If bookingsData is provided, use it; otherwise fetch all bookings
    const bookings = bookingsData || await fetchAllBookings(true);

    if (!bookings || bookings.length === 0) {
      alert('No bookings to export');
      return;
    }

    // Create a single sheet with all data - one row per passenger with booking info
    const allData = [];
    
    bookings.forEach((booking) => {

      if (booking.passengers && booking.passengers.length > 0) {
        // Add one row per passenger with booking info
        booking.passengers.forEach((passenger) => {
          allData.push({
            'Full Name': passenger.full_name || '',
            'Booking Reference': booking.booking_reference || '',
            'Gender': passenger.gender || '',
            'Date of Birth': formatDateForExcel(passenger.date_of_birth),
            'Email': passenger.email || '',
            'Phone Number': passenger.phone_number || '',
            'Address': passenger.address || '',
            'Zip Code': passenger.zip_code || '',
            'City': passenger.city || '',
            'Social Security Number': passenger.social_security_number || '',
            'Passport Number': passenger.passport_number || '',
            'Passport Country': passenger.passport_country || '',
            'Passport Issue Date': formatDateForExcel(passenger.passport_issue_date),
            'Passport Expiry Date': formatDateForExcel(passenger.passport_expiration_date),
            'Type': booking.type || '',
            'Month': booking.month || '',
            'Departure City': booking.departure_city || '',
            'Total Price': formatPriceForExcel(booking.total_price),
            'Status': booking.status || '',
            'Payment Status': booking.payment_status || '',
            'Adult 4-bed Tickets': booking.tickets?.adult_4bed || 0,
            'Adult 3-bed Tickets': booking.tickets?.adult_3bed || 0,
            'Adult 2-bed Tickets': booking.tickets?.adult_2bed || 0,
            'Child 4-bed Tickets': booking.tickets?.child_4bed || 0,
            'Child 3-bed Tickets': booking.tickets?.child_3bed || 0,
            'Child 2-bed Tickets': booking.tickets?.child_2bed || 0,
            'Infant Tickets': booking.tickets?.infant || 0,
            'Total Tickets': booking.tickets?.total_tickets || 0,
            'Emergency Contact Name': passenger.emergency_contact_name || '',
            'Emergency Contact Relationship': passenger.emergency_contact_relationship || '',
            'Emergency Contact Number': passenger.emergency_contact_number || '',
            'Previous Hajj/Umrah': passenger.previous_hajj_umrah || '',
            'Special Needs': passenger.special_needs || '',
            'Created At': formatDateForExcel(booking.created_at),
          });
        });
      } else {
        // Add one row for booking with no passengers
        allData.push({
          'Full Name': 'No passengers',
          'Booking Reference': booking.booking_reference || '',
          'Gender': '',
          'Date of Birth': '',
          'Email': '',
          'Phone Number': '',
          'Address': '',
          'Zip Code': '',
          'City': '',
          'Social Security Number': '',
          'Passport Number': '',
          'Passport Country': '',
          'Passport Issue Date': '',
          'Passport Expiry Date': '',
          'Type': booking.type || '',
          'Month': booking.month || '',
          'Destination': booking.destination || '',
          'Total Price': formatPriceForExcel(booking.total_price),
          'Status': booking.status || '',
          'Payment Status': booking.payment_status || '',
          'Adult 4-bed Tickets': booking.tickets?.adult_4bed || 0,
          'Adult 3-bed Tickets': booking.tickets?.adult_3bed || 0,
          'Adult 2-bed Tickets': booking.tickets?.adult_2bed || 0,
          'Child 4-bed Tickets': booking.tickets?.child_4bed || 0,
          'Child 3-bed Tickets': booking.tickets?.child_3bed || 0,
          'Child 2-bed Tickets': booking.tickets?.child_2bed || 0,
          'Infant Tickets': booking.tickets?.infant || 0,
          'Total Tickets': booking.tickets?.total_tickets || 0,
          'Emergency Contact Name': '',
          'Emergency Contact Relationship': '',
          'Emergency Contact Number': '',
          'Previous Hajj/Umrah': '',
          'Special Needs': '',
          'Created At': formatDateForExcel(booking.created_at),
        });
      }
    });

    // Create workbook with single sheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(allData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Bookings Data');

    // Set column widths for better readability (in the exact order specified)
    const colWidths = [
      { wch: 25 }, // Full Name
      { wch: 35 }, // Booking Reference
      { wch: 10 }, // Gender
      { wch: 15 }, // Date of Birth
      { wch: 30 }, // Email
      { wch: 15 }, // Phone Number
      { wch: 30 }, // Address
      { wch: 12 }, // Zip Code
      { wch: 15 }, // City
      { wch: 20 }, // Social Security Number
      { wch: 18 }, // Passport Number
      { wch: 15 }, // Passport Country
      { wch: 18 }, // Passport Issue Date
      { wch: 18 }, // Passport Expiry Date
      { wch: 12 }, // Type
      { wch: 15 }, // Month
      { wch: 20 }, // Departure City
      { wch: 15 }, // Total Price
      { wch: 12 }, // Status
      { wch: 15 }, // Payment Status
      { wch: 15 }, // Adult 4-bed Tickets
      { wch: 15 }, // Adult 3-bed Tickets
      { wch: 15 }, // Adult 2-bed Tickets
      { wch: 15 }, // Child 4-bed Tickets
      { wch: 15 }, // Child 3-bed Tickets
      { wch: 15 }, // Child 2-bed Tickets
      { wch: 15 }, // Infant Tickets
      { wch: 15 }, // Total Tickets
      { wch: 20 }, // Emergency Contact Name
      { wch: 20 }, // Emergency Contact Relationship
      { wch: 18 }, // Emergency Contact Number
      { wch: 18 }, // Previous Hajj/Umrah
      { wch: 20 }, // Special Needs
      { wch: 18 }, // Created At
    ];
    worksheet['!cols'] = colWidths;

    // Generate filename with current date and optional package name
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    let filename;
    if (packageName) {
      const sanitizedPackageName = packageName.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      filename = `${sanitizedPackageName}_bookings_${dateStr}.xlsx`;
    } else {
      filename = `bookings_export_${dateStr}.xlsx`;
    }

    // Write and download file
    XLSX.writeFile(workbook, filename);

    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

/**
 * Export bookings for a specific package to Excel
 * @param {number} packageId - The ID of the package
 * @param {string} packageName - The name of the package (for filename)
 */
export const exportPackageBookingsToExcel = async (packageId, packageName = 'Package') => {
  try {
    const bookings = await fetchBookingsByPackageId(packageId);

    if (!bookings || bookings.length === 0) {
      alert('No bookings to export for this package');
      return;
    }

    // Create a single sheet with all data - one row per passenger with booking info
    const allData = [];
    
    bookings.forEach((booking) => {
      // Handle both camelCase and snake_case field names
      const bookingRef = booking.bookingReference || booking.booking_reference || '';
      const bookingType = booking.type || '';
      const bookingMonth = booking.month || '';
      const departureCity = booking.departureCity || booking.departure_city || '';
      const totalPrice = booking.totalPrice || booking.total_price || 0;
      const status = booking.status || '';
      const paymentStatus = booking.paymentStatus || booking.payment_status || '';
      const createdAt = booking.createdAt || booking.created_at || '';

      // Handle tickets - support both camelCase and snake_case
      const tickets = booking.tickets || {};
      const adult4bed = tickets.adult4bed || tickets.adult_4bed || 0;
      const adult3bed = tickets.adult3bed || tickets.adult_3bed || 0;
      const adult2bed = tickets.adult2bed || tickets.adult_2bed || 0;
      const child4bed = tickets.child4bed || tickets.child_4bed || 0;
      const child3bed = tickets.child3bed || tickets.child_3bed || 0;
      const child2bed = tickets.child2bed || tickets.child_2bed || 0;
      const infant = tickets.infant || 0;
      const totalTickets = tickets.totalTickets || tickets.total_tickets || 0;

      if (booking.passengers && booking.passengers.length > 0) {
        // Add one row per passenger with booking info
        booking.passengers.forEach((passenger) => {
          allData.push({
            'Full Name': passenger.full_name || '',
            'Booking Reference': bookingRef,
            'Gender': passenger.gender || '',
            'Date of Birth': formatDateForExcel(passenger.date_of_birth),
            'Email': passenger.email || '',
            'Phone Number': passenger.phone_number || '',
            'Address': passenger.address || '',
            'Zip Code': passenger.zip_code || '',
            'City': passenger.city || '',
            'Social Security Number': passenger.social_security_number || '',
            'Passport Number': passenger.passport_number || '',
            'Passport Country': passenger.passport_country || '',
            'Passport Issue Date': formatDateForExcel(passenger.passport_issue_date),
            'Passport Expiry Date': formatDateForExcel(passenger.passport_expiration_date),
            'Type': bookingType,
            'Month': bookingMonth,
            'Departure City': departureCity,
            'Total Price': formatPriceForExcel(totalPrice),
            'Status': status,
            'Payment Status': paymentStatus,
            'Adult 4-bed Tickets': adult4bed,
            'Adult 3-bed Tickets': adult3bed,
            'Adult 2-bed Tickets': adult2bed,
            'Child 4-bed Tickets': child4bed,
            'Child 3-bed Tickets': child3bed,
            'Child 2-bed Tickets': child2bed,
            'Infant Tickets': infant,
            'Total Tickets': totalTickets,
            'Emergency Contact Name': passenger.emergency_contact_name || '',
            'Emergency Contact Relationship': passenger.emergency_contact_relationship || '',
            'Emergency Contact Number': passenger.emergency_contact_number || '',
            'Previous Hajj/Umrah': passenger.previous_hajj_umrah || '',
            'Special Needs': passenger.special_needs || '',
            'Created At': formatDateForExcel(createdAt),
          });
        });
      } else {
        // Add one row for booking with no passengers
        allData.push({
          'Full Name': 'No passengers',
          'Booking Reference': bookingRef,
          'Gender': '',
          'Date of Birth': '',
          'Email': '',
          'Phone Number': '',
          'Address': '',
          'Zip Code': '',
          'City': '',
          'Social Security Number': '',
          'Passport Number': '',
          'Passport Country': '',
          'Passport Issue Date': '',
          'Passport Expiry Date': '',
          'Month': bookingMonth,
          'Departure City': departureCity,
          'Total Price': formatPriceForExcel(totalPrice),
          'Status': status,
          'Payment Status': paymentStatus,
          'Adult 4-bed Tickets': adult4bed,
          'Adult 3-bed Tickets': adult3bed,
          'Adult 2-bed Tickets': adult2bed,
          'Child 4-bed Tickets': child4bed,
          'Child 3-bed Tickets': child3bed,
          'Child 2-bed Tickets': child2bed,
          'Infant Tickets': infant,
          'Total Tickets': totalTickets,
          'Emergency Contact Name': '',
          'Emergency Contact Relationship': '',
          'Emergency Contact Number': '',
          'Previous Hajj/Umrah': '',
          'Special Needs': '',
          'Created At': formatDateForExcel(createdAt),
        });
      }
    });

    // Create workbook with single sheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(allData);
    
    // Sanitize package name for filename
    const sanitizedPackageName = packageName.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Package Bookings');

    // Set column widths for better readability (in the exact order specified)
    const colWidths = [
      { wch: 25 }, // Full Name
      { wch: 35 }, // Booking Reference
      { wch: 10 }, // Gender
      { wch: 15 }, // Date of Birth
      { wch: 30 }, // Email
      { wch: 15 }, // Phone Number
      { wch: 35 }, // Address
      { wch: 12 }, // Zip Code
      { wch: 15 }, // City
      { wch: 25 }, // Social Security Number
      { wch: 30 }, // Passport Number
      { wch: 15 }, // Passport Country
      { wch: 18 }, // Passport Issue Date
      { wch: 18 }, // Passport Expiry Date
      { wch: 15 }, // Month
      { wch: 20 }, // Departure City
      { wch: 15 }, // Total Price
      { wch: 12 }, // Status
      { wch: 15 }, // Payment Status
      { wch: 15 }, // Adult 4-bed Tickets
      { wch: 15 }, // Adult 3-bed Tickets
      { wch: 15 }, // Adult 2-bed Tickets
      { wch: 15 }, // Child 4-bed Tickets
      { wch: 15 }, // Child 3-bed Tickets
      { wch: 15 }, // Child 2-bed Tickets
      { wch: 15 }, // Infant Tickets
      { wch: 15 }, // Total Tickets
      { wch: 25 }, // Emergency Contact Name
      { wch: 25 }, // Emergency Contact Relationship
      { wch: 18 }, // Emergency Contact Number
      { wch: 18 }, // Previous Hajj/Umrah
      { wch: 25 }, // Special Needs
      { wch: 18 }, // Created At
    ];
    worksheet['!cols'] = colWidths;

    // Generate filename with package name and current date
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const filename = `${sanitizedPackageName}_bookings_${dateStr}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);

    return true;
  } catch (error) {
    console.error('Error exporting package bookings to Excel:', error);
    alert(`Failed to export bookings: ${error.message}`);
    throw error;
  }
};
